import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { AlertTriangle, Clock, FileQuestion, MessageCircle } from "lucide-react";

const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const painPoints = [
    {
      icon: FileQuestion,
      title: "The Renaming Nightmare",
      description:
        "Your highly paid staff waste hours every week renaming IMG-2024-WA001.jpg to something usable. That's not what you hired them for.",
    },
    {
      icon: MessageCircle,
      title: 'The "Shoebox" Effect',
      description:
        "Digital payments mixed with lost photos, leading to 'Missing Information' queries during audit season. Every. Single. Year.",
    },
    {
      icon: Clock,
      title: "Deadline Anxiety",
      description:
        "Partners not knowing if documents were received until 48 hours before the GST deadline. The panic is real.",
    },
    {
      icon: AlertTriangle,
      title: "The Chasing Culture",
      description:
        "Your team spends more time chasing clients for documents than actually doing compliance work.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      id="problem"
      ref={ref}
      className="py-20 md:py-32 bg-navy relative overflow-hidden"
      data-testid="problem-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,_white_1px,_transparent_1px)] bg-[length:60px_60px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <p className="font-body text-sm font-semibold uppercase tracking-widest text-gold mb-4">
            The Reality Check
          </p>
          <h2
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight mb-6"
            data-testid="problem-headline"
          >
            Is Your Firm Running on
            <br />
            <span className="text-gold">WhatsApp Forwards & Panic?</span>
          </h2>
          <p className="font-body text-base md:text-lg text-white/70 max-w-2xl mx-auto">
            Every CA firm knows the drill. Clients send documents at 11 PM. 
            Articles forget to save them. You waste hours searching for "that one PDF" 
            during audit season.
          </p>
        </motion.div>

        {/* Pain Points Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 gap-6 md:gap-8"
        >
          {painPoints.map((point, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 md:p-8 hover:bg-white/10 transition-all duration-300"
              data-testid={`pain-point-${index}`}
            >
              <div className="flex items-start gap-5">
                <div className="flex-shrink-0 w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center group-hover:bg-gold/30 transition-colors duration-300">
                  <point.icon size={24} className="text-gold" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-white mb-3">
                    {point.title}
                  </h3>
                  <p className="font-body text-white/70 leading-relaxed">
                    {point.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <p className="font-body text-lg md:text-xl text-white/80 italic">
            "The manual grind isn't just frustrating—
            <span className="text-gold font-semibold"> it's costing you partners' billable hours.</span>"
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
